import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
/** @jsx jsx */
import { useRef } from 'react';
import { jsx } from '@emotion/react';
import uuid from 'uuid/v4';
import { FabricEditorAnalyticsContext } from '@atlaskit/analytics-namespaced-context';
import { ACTION, fireAnalyticsEvent } from '@atlaskit/editor-common/analytics';
import { EditorExperience, ExperienceStore } from '@atlaskit/editor-common/ufo';
import { getAnalyticsAppearance, startMeasure, stopMeasure } from '@atlaskit/editor-common/utils';
import { basePlugin } from '../plugins';
import EditorActions from '../actions';
import { createFeatureFlagsFromProps } from '../create-editor/feature-flags-from-props';
import { name, version } from '../version-wrapper';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { useConstructor } from '@atlaskit/editor-common/hooks';
import { useEditorContext } from '../ui/EditorContext';
import measurements from '../utils/performance/measure-enum';
import EditorInternal from './editor-internal';
import sendDurationAnalytics from './utils/sendDurationAnalytics';
import trackEditorActions from './utils/trackEditorActions';
export function Editor(passedProps) {
  var _editorContext$editor;
  var defaultProps = {
    appearance: 'comment',
    disabled: false,
    quickInsert: true
  };
  var props = _objectSpread(_objectSpread({}, defaultProps), passedProps);
  var editorContext = useEditorContext();
  var editorActions = useRef((_editorContext$editor = editorContext === null || editorContext === void 0 ? void 0 : editorContext.editorActions) !== null && _editorContext$editor !== void 0 ? _editorContext$editor : new EditorActions());
  var startTime = useRef(performance.now());
  var _useAnalyticsEvents = useAnalyticsEvents(),
    createAnalyticsEvent = _useAnalyticsEvents.createAnalyticsEvent;
  var experienceStore = useRef();
  var handleAnalyticsEvent = function handleAnalyticsEvent(data) {
    return fireAnalyticsEvent(createAnalyticsEvent)(data);
  };
  useConstructor(function () {
    trackEditorActions(editorActions.current, props.performanceTracking, function (value) {
      return handleAnalyticsEvent(value);
    });
  });
  var getExperienceStore = function getExperienceStore() {
    return experienceStore.current;
  };
  var onEditorCreated = function onEditorCreated(instance) {
    var _props$featureFlags;
    editorActions.current._privateRegisterEditor(instance.view, instance.eventDispatcher, instance.transformer, function () {
      return createFeatureFlagsFromProps(props);
    });
    if ((_props$featureFlags = props.featureFlags) !== null && _props$featureFlags !== void 0 && _props$featureFlags.ufo) {
      var _experienceStore$curr;
      experienceStore.current = ExperienceStore.getInstance(instance.view);
      (_experienceStore$curr = experienceStore.current) === null || _experienceStore$curr === void 0 ? void 0 : _experienceStore$curr.start(EditorExperience.loadEditor, startTime.current);
    }
    if (props.onEditorReady) {
      var _props$performanceTra, _props$performanceTra2, _props$featureFlags2;
      var measureEditorReady = (props === null || props === void 0 ? void 0 : (_props$performanceTra = props.performanceTracking) === null || _props$performanceTra === void 0 ? void 0 : (_props$performanceTra2 = _props$performanceTra.onEditorReadyCallbackTracking) === null || _props$performanceTra2 === void 0 ? void 0 : _props$performanceTra2.enabled) || ((_props$featureFlags2 = props.featureFlags) === null || _props$featureFlags2 === void 0 ? void 0 : _props$featureFlags2.ufo);
      measureEditorReady && startMeasure(measurements.ON_EDITOR_READY_CALLBACK);
      props.onEditorReady(editorActions.current);
      measureEditorReady && stopMeasure(measurements.ON_EDITOR_READY_CALLBACK, sendDurationAnalytics(ACTION.ON_EDITOR_READY_CALLBACK, props, getExperienceStore, createAnalyticsEvent));
    }
  };
  var onEditorDestroyed = function onEditorDestroyed(_instance) {
    var _props$onDestroy;
    editorActions.current._privateUnregisterEditor();
    (_props$onDestroy = props.onDestroy) === null || _props$onDestroy === void 0 ? void 0 : _props$onDestroy.call(props);
  };
  return jsx(EditorInternal, {
    props: props,
    handleAnalyticsEvent: handleAnalyticsEvent,
    createAnalyticsEvent: createAnalyticsEvent,
    preset: props.preset,
    handleSave: function handleSave(view) {
      var _props$onSave;
      return (_props$onSave = props.onSave) === null || _props$onSave === void 0 ? void 0 : _props$onSave.call(props, view);
    },
    editorActions: editorActions.current,
    getExperienceStore: getExperienceStore,
    onEditorCreated: onEditorCreated,
    onEditorDestroyed: onEditorDestroyed
  });
}
export default function EditorNext(props) {
  var editorSessionId = useRef(uuid());
  return jsx(FabricEditorAnalyticsContext, {
    data: {
      packageName: name,
      packageVersion: version,
      componentName: 'editorCore',
      appearance: getAnalyticsAppearance(props.appearance),
      editorSessionId: editorSessionId.current
    }
  }, jsx(Editor, props));
}
EditorNext.propTypes = {
  minHeight: function minHeight(_ref) {
    var appearance = _ref.appearance,
      _minHeight = _ref.minHeight;
    if (_minHeight && appearance && !['comment', 'chromeless'].includes(appearance)) {
      return new Error('minHeight only supports editor appearance chromeless and comment for Editor');
    }
    return null;
  },
  preset: function preset(_ref2) {
    var _preset = _ref2.preset;
    if (!_preset.has(basePlugin)) {
      return new Error('Presets must contain the base plugin');
    }
    return null;
  }
};